import {
  EXERCISE_CATEGORY,
  EXERCISE_CATEGORY_LABELS,
  TRAINING_LIMITS,
  TRAINING_VIDEO_LIMITS,
  type ExerciseCategory,
  type ExerciseInput,
  type TrainingDiagram,
  type TrainingExerciseDto,
} from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { AGE_CLASSES, parseTags } from '../../lib/training';
import { useUnsavedChanges } from '../../lib/useUnsavedChanges';
import { TabPanels, type TabPanelItem } from '../TabPanels';
import { Button, Dialog, Field, Input, Select, Textarea, useToast } from '../ui';
import { DiagramField } from './DiagramField';
import { ExerciseLoadFields } from './ExerciseLoadFields';
import { ExerciseVideoManager } from './ExerciseVideos';

/**
 * Formular fuer eine eigene Uebung oder eine Linkuebung.
 *
 * Wird nur gemountet, solange es offen ist: Jede Oeffnung beginnt mit frischem
 * Stand. Die Skizze bearbeitet `DiagramField` (Skizzeneditor).
 *
 * Aufgeteilt in Reiter (Allgemein, Ablauf, Skizze, Videos), damit nicht alles auf einer Seite
 * liegt; Speichern und Abbrechen stehen im festen Fuss des Dialogs auf jedem Reiter. Eine
 * Linkuebung hat nur die allgemeinen Angaben und damit keine Reiter.
 */

type TabId = 'general' | 'procedure' | 'sketch' | 'videos';
const TAB_ORDER: TabId[] = ['general', 'procedure', 'sketch', 'videos'];

/** Reiter, auf dem das Feld mit dem Fehlerschluessel liegt; Unbekanntes landet bei Allgemein. */
const TAB_OF_FIELD: Record<string, TabId> = { setup: 'procedure', procedure: 'procedure', coaching: 'procedure', variations: 'procedure', diagram: 'sketch' };
const tabOfField = (field: string): TabId => TAB_OF_FIELD[field] ?? 'general';

interface Draft {
  title: string;
  category: ExerciseCategory;
  focus: string;
  ageMin: string;
  ageMax: string;
  playersMin: string;
  playersMax: string;
  durationMinutes: string;
  durationMaxMinutes: string;
  sets: string;
  setMinutes: string;
  pauseMinutes: string;
  material: string;
  setup: string;
  procedure: string;
  coaching: string;
  variations: string;
  diagram: TrainingDiagram | null;
  linkUrl: string;
}

function draftFrom(exercise: TrainingExerciseDto | null): Draft {
  return {
    title: exercise?.title ?? '',
    category: exercise?.category ?? 'TECHNIQUE',
    focus: exercise?.focus.join(', ') ?? '',
    ageMin: exercise?.ageMin?.toString() ?? '',
    ageMax: exercise?.ageMax?.toString() ?? '',
    playersMin: exercise?.playersMin?.toString() ?? '',
    playersMax: exercise?.playersMax?.toString() ?? '',
    durationMinutes: exercise?.durationMinutes?.toString() ?? '',
    durationMaxMinutes: exercise?.durationMaxMinutes?.toString() ?? '',
    sets: exercise?.sets?.toString() ?? '',
    setMinutes: exercise?.setMinutes?.toString() ?? '',
    pauseMinutes: exercise?.pauseMinutes?.toString() ?? '',
    material: exercise?.material ?? '',
    setup: exercise?.setup ?? '',
    procedure: exercise?.procedure ?? '',
    coaching: exercise?.coaching ?? '',
    variations: exercise?.variations ?? '',
    diagram: exercise?.diagram ?? null,
    linkUrl: exercise?.linkUrl ?? '',
  };
}

const number = (text: string): number | null => (text.trim() === '' ? null : Number(text));

/** Die offensichtlichen Fehler der empfohlenen Dauer, bevor etwas verschickt wird (der Server prueft alles noch einmal). */
function loadProblems(draft: Draft, link: boolean): Record<string, string> {
  const found: Record<string, string> = {};
  const from = number(draft.durationMinutes);
  const to = number(draft.durationMaxMinutes);
  if (to !== null && from === null) found['durationMaxMinutes'] = 'Bitte gib auch „von“ an.';
  else if (to !== null && from !== null && to < from) found['durationMaxMinutes'] = 'Die Dauer „bis“ liegt unter „von“.';
  if (link) return found;
  const sets = number(draft.sets);
  const perSet = number(draft.setMinutes);
  const pause = number(draft.pauseMinutes);
  if (sets !== null && perSet === null) found['setMinutes'] = 'Bitte gib die Minuten je Durchgang an.';
  if (perSet !== null && sets === null) found['sets'] = 'Bitte gib die Anzahl der Durchgänge an.';
  if (pause !== null && pause > 0 && sets === null) found['pauseMinutes'] = 'Eine Pause gehört zu Durchgängen.';
  else if (pause !== null && pause > 0 && sets === 1) found['pauseMinutes'] = 'Bei einem Durchgang gibt es keine Pause.';
  return found;
}

function toInput(draft: Draft, link: boolean): ExerciseInput {
  return {
    title: draft.title.trim(),
    category: draft.category,
    focus: parseTags(draft.focus),
    ageMin: number(draft.ageMin),
    ageMax: number(draft.ageMax),
    playersMin: number(draft.playersMin),
    playersMax: number(draft.playersMax),
    durationMinutes: number(draft.durationMinutes),
    durationMaxMinutes: number(draft.durationMaxMinutes),
    // Eine Linkuebung hat keine Durchgaenge.
    sets: link ? null : number(draft.sets),
    setMinutes: link ? null : number(draft.setMinutes),
    pauseMinutes: link ? null : number(draft.pauseMinutes),
    material: link ? '' : draft.material.trim(),
    setup: link ? '' : draft.setup.trim(),
    procedure: link ? '' : draft.procedure.trim(),
    coaching: link ? '' : draft.coaching.trim(),
    variations: link ? '' : draft.variations.trim(),
    diagram: link ? null : draft.diagram,
    linkUrl: link ? draft.linkUrl.trim() : null,
  };
}

export function ExerciseFormDialog({
  exercise,
  link,
  onClose,
  onSaved,
}: {
  /** Vorhandene Uebung zum Bearbeiten; null = neu. */
  exercise: TrainingExerciseDto | null;
  /** Linkuebung statt eigener Uebung (beim Bearbeiten folgt es der Uebung). */
  link: boolean;
  onClose: () => void;
  onSaved?: (saved: TrainingExerciseDto) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [initial] = useState(() => draftFrom(exercise));
  const [draft, setDraft] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<TabId>('general');
  const isLink = exercise ? exercise.isLink : link;
  const set = (patch: Partial<Draft>): void => setDraft((current) => ({ ...current, ...patch }));
  const errorTabs = new Set<string>(Object.keys(errors).map(tabOfField));

  // Fehler anzeigen und zum ersten Reiter springen, der einen enthaelt.
  const showErrors = (found: Record<string, string>): void => {
    setErrors(found);
    const first = TAB_ORDER.find((id) => Object.keys(found).some((field) => tabOfField(field) === id));
    if (first) setTab(first);
  };

  const save = useMutation({
    mutationFn: () => {
      const input = toInput(draft, isLink);
      return exercise
        ? api.patch<TrainingExerciseDto>(`/api/training/exercises/${exercise.id}`, input)
        : api.post<TrainingExerciseDto>('/api/training/exercises', input);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['training-exercises'] });
      await queryClient.invalidateQueries({ queryKey: ['training-exercise'] });
      toast.show(exercise ? 'Übung gespeichert' : isLink ? 'Linkübung angelegt' : 'Übung angelegt');
      onSaved?.(saved);
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        showErrors(error.fieldErrors());
        toast.show(error.message, 'error');
      } else {
        toast.show('Speichern fehlgeschlagen', 'error');
      }
    },
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const requestClose = useUnsavedChanges(true, dirty, onClose, save.isPending);

  const submit = (): void => {
    const found: Record<string, string> = {};
    if (!draft.title.trim()) found['title'] = 'Bitte gib einen Titel an.';
    if (isLink && !/^https?:\/\/\S+$/i.test(draft.linkUrl.trim())) found['linkUrl'] = 'Bitte gib eine Adresse mit http:// oder https:// an.';
    if (draft.ageMin && draft.ageMax && Number(draft.ageMin) > Number(draft.ageMax)) found['ageMax'] = 'Die Altersklasse „bis“ liegt vor „von“.';
    if (draft.playersMin && draft.playersMax && Number(draft.playersMin) > Number(draft.playersMax)) found['playersMax'] = 'Die Spielerzahl „bis“ liegt unter „von“.';
    Object.assign(found, loadProblems(draft, isLink));
    showErrors(found);
    if (Object.keys(found).length === 0) save.mutate();
  };

  const ageSelect = (id: string, value: string, onChange: (value: string) => void): JSX.Element => (
    <Select id={id} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">–</option>
      {AGE_CLASSES.map((age) => (
        <option key={age} value={age}>U{age}</option>
      ))}
    </Select>
  );

  // Die Felder nach Reitern.
  const general = (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        Mit <span className="font-semibold text-rose-600">*</span> markierte Felder sind Pflicht, alles andere ist freiwillig.
      </p>
      <Field label="Titel" required error={errors['title']}>
        {(id) => <Input id={id} value={draft.title} maxLength={TRAINING_LIMITS.title} onChange={(event) => set({ title: event.target.value })} autoFocus />}
      </Field>

      {isLink && (
        <Field label="Adresse" required error={errors['linkUrl']} hint="z. B. https://www.youtube.com/…">
          {(id) => <Input id={id} type="url" inputMode="url" value={draft.linkUrl} maxLength={TRAINING_LIMITS.link} onChange={(event) => set({ linkUrl: event.target.value })} />}
        </Field>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Kategorie" required error={errors['category']}>
          {(id) => (
            <Select id={id} value={draft.category} onChange={(event) => set({ category: event.target.value as ExerciseCategory })}>
              {EXERCISE_CATEGORY.map((category) => (
                <option key={category} value={category}>{EXERCISE_CATEGORY_LABELS[category]}</option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Schwerpunkte" error={errors['focus']} hint="Mit Komma trennen, z. B. Passen, Dribbling">
          {(id) => <Input id={id} value={draft.focus} onChange={(event) => set({ focus: event.target.value })} />}
        </Field>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-slate-900">Für wen?</legend>
        <p className="text-xs text-slate-500">Altersklasse und Spielerzahl, für die die Übung gedacht ist – damit sie sich später besser finden lässt.</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Alter von" error={errors['ageMin']}>{(id) => ageSelect(id, draft.ageMin, (value) => set({ ageMin: value }))}</Field>
          <Field label="Alter bis" error={errors['ageMax']}>{(id) => ageSelect(id, draft.ageMax, (value) => set({ ageMax: value }))}</Field>
          <Field label="Spieler von" error={errors['playersMin']}>
            {(id) => <Input id={id} type="number" inputMode="numeric" min={1} max={50} value={draft.playersMin} onChange={(event) => set({ playersMin: event.target.value })} />}
          </Field>
          <Field label="Spieler bis" error={errors['playersMax']}>
            {(id) => <Input id={id} type="number" inputMode="numeric" min={1} max={50} value={draft.playersMax} onChange={(event) => set({ playersMax: event.target.value })} />}
          </Field>
        </div>
      </fieldset>

      <ExerciseLoadFields
        value={draft}
        errors={errors}
        withSets={!isLink}
        onChange={(patch) => set(patch)}
      />

      {!isLink && (
        <Field label="Material" error={errors['material']} hint="Was für den Aufbau gebraucht wird.">
          {(id) => <Input id={id} value={draft.material} maxLength={TRAINING_LIMITS.material} onChange={(event) => set({ material: event.target.value })} placeholder="z. B. 8 Hütchen, 4 Bälle, Leibchen" />}
        </Field>
      )}
    </div>
  );

  const procedure = (
    <div className="space-y-4">
      <Field label="Organisation" error={errors['setup']} hint="Aufbau: Feldgröße, Gruppen, Positionen">
        {(id) => <Textarea id={id} rows={3} value={draft.setup} maxLength={TRAINING_LIMITS.text} onChange={(event) => set({ setup: event.target.value })} />}
      </Field>
      <Field label="Ablauf" error={errors['procedure']} hint="Schritt für Schritt: was die Spieler tun">
        {(id) => <Textarea id={id} rows={4} value={draft.procedure} maxLength={TRAINING_LIMITS.text} onChange={(event) => set({ procedure: event.target.value })} />}
      </Field>
      <Field label="Coaching" error={errors['coaching']} hint="Worauf der Trainer achtet und was er ansagt">
        {(id) => <Textarea id={id} rows={3} value={draft.coaching} maxLength={TRAINING_LIMITS.text} onChange={(event) => set({ coaching: event.target.value })} />}
      </Field>
      <Field label="Varianten" error={errors['variations']} hint="Leichter oder schwerer machen, andere Spielformen">
        {(id) => <Textarea id={id} rows={3} value={draft.variations} maxLength={TRAINING_LIMITS.text} onChange={(event) => set({ variations: event.target.value })} />}
      </Field>
    </div>
  );

  // Skizzeneditor: legt die Skizze an, zeichnet und entfernt sie. Am Handy ohne Rand, ueber die ganze Breite.
  const sketch = (
    <div>
      <DiagramField title={draft.title} value={draft.diagram} onChange={(diagram) => set({ diagram })} disabled={save.isPending} flush />
      {errors['diagram'] && <p className="error-text px-3 sm:px-0">{errors['diagram']}</p>}
    </div>
  );

  // Videos gehoeren zur gespeicherten Uebung (der Upload geht an ihre Adresse): bei einer neuen steht nur der Hinweis.
  const videos = exercise?.editable
    ? <FormVideos exercise={exercise} />
    : (
      <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
        Erst speichern: Videos gehören zur gespeicherten Übung. Danach kannst du hier unter „Videos“ bis zu {TRAINING_VIDEO_LIMITS.perExercise} Videos hochladen.
      </p>
    );

  const tabs: TabPanelItem[] = [
    { id: 'general', label: 'Allgemein', content: general },
    { id: 'procedure', label: 'Ablauf', content: procedure },
    { id: 'sketch', label: 'Skizze', content: sketch, bleed: true },
    { id: 'videos', label: 'Videos', content: videos, lazy: true, muted: !exercise?.editable },
  ].map((item) => ({ ...item, invalid: errorTabs.has(item.id) }));

  const notice = (
    <>
      {isLink && (
        <p className="mb-4 text-sm text-slate-600">
          Eine Linkübung verweist nur auf eine Seite im Netz (zum Beispiel ein Video oder eine Übungssammlung). Es wird nichts kopiert.
        </p>
      )}
      {Object.keys(errors).length > 0 && (
        <p role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">Bitte prüfe die markierten Angaben. Deine Eingaben bleiben erhalten.</p>
      )}
    </>
  );

  return (
    <Dialog
      open
      onClose={requestClose}
      title={exercise ? (isLink ? 'Linkübung bearbeiten' : 'Übung bearbeiten') : isLink ? 'Neue Linkübung' : 'Neue Übung'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={requestClose} disabled={save.isPending}>Abbrechen</Button>
          <Button onClick={submit} loading={save.isPending}>Speichern</Button>
        </>
      }
    >
      <fieldset disabled={save.isPending} className="min-w-0">
        {isLink ? (
          // Eine Linkuebung hat nur die allgemeinen Angaben: keine Reiter.
          <div className="space-y-4">
            {notice}
            {general}
          </div>
        ) : (
          <TabPanels label="Bereiche der Übung" tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} notice={notice} />
        )}
      </fieldset>
    </Dialog>
  );
}

/** Videos der gespeicherten Uebung, frisch geladen: Hochladen und Loeschen wirken sofort, nicht erst beim Speichern. */
function FormVideos({ exercise }: { exercise: TrainingExerciseDto }): JSX.Element {
  const { data } = useQuery({
    queryKey: ['training-exercise', exercise.id],
    queryFn: () => api.get<TrainingExerciseDto>(`/api/training/exercises/${exercise.id}`),
    initialData: exercise,
  });
  return <ExerciseVideoManager exercise={data} />;
}
